import type { RecommendationLayoutSeed } from 'src/pages/Product/functions/recommendationLayoutSeed'
import { previewStaticPaths } from 'scripts/preview-routes.cjs'
/* Next */
import { GetStaticPaths, GetStaticProps } from 'next'

/* Types */
import { hasOwnProperty } from 'src/functions/typeGuards'
import { ParsedUrlQuery } from 'querystring'
import { tProduct } from 'src/shared/frontendTypes/product'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'

/* Functions */
import { post } from 'src/functions/fetch'
import { saveUrl } from 'src/functions/regex'
import { loadProductPageData } from 'src/functions/pageData'

/* Components */
import Product from 'src/pages/Product/index'

/* Options */
import config from 'src/config'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

interface Props {
  product: tProduct
  recommendationsDeferred?: boolean
  recommendationsLayoutSeed?: RecommendationLayoutSeed
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const ProductPage: React.FC<Props> = ({ product, recommendationsDeferred, recommendationsLayoutSeed, landingPageImageAiGenerated }:Props) =>
  <Product {...{ product, recommendationsDeferred, recommendationsLayoutSeed, landingPageImageAiGenerated }} />

export default ProductPage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/*                              Static Paths & Props                               */
/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

interface IParams extends ParsedUrlQuery {
  pid: string
  alias: string
}

const productBuildMode = () =>
  String(process.env.PRODUCT_BUILD_MODE || '').trim().toLowerCase()

const productBuildSize = () => {
  switch (productBuildMode()) {
    case 'full': return config.cache_static_products_size
    case 'quick': return process.env.BUILD ? config.cache_static_products_size : 0
    default:
      switch(process.env.CACHE) {
        case 'static': return config.cache_static_products_size
        case 'ssr'   : return config.cache_ssr_products_size
        default      : return 1
      }
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const preview = previewStaticPaths('product')
  if (preview) return preview


  const size = productBuildSize()

  if (size <= 0) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const data = await post({
    url:'build/productsUrls', 
    body:{ 
      ...config.shop, 
      size,
      token: process.env.BUILD_TOKEN,
    }, 
    local:true
  })
  
  type item = { id: number; path: string }
  const isItems = (arg: unknown): arg is item[] =>
    Array.isArray(arg) &&
    arg.every(obj =>
      typeof obj === 'object' && obj !== null &&
      hasOwnProperty(obj,'id') && typeof obj.id === 'number' &&
      hasOwnProperty(obj,'path') && typeof obj.path === 'string'
    )

  if (!isItems(data)) {
    console.error('Kann Produkt-URLs nicht laden!', data)
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  const paths = data.map(({ id, path }: item) => {
    const pid = id.toString()
    const alias = saveUrl(path || pid)

    return {
      params: { pid, alias },
    }
  })

  return {
    paths: paths,
    fallback: process.env.BUILD ? false : 'blocking',
  }

}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) =>
  loadProductPageData(params as IParams)
